// Sessions as the unit a query matches. The query language is written over
// requests (lib/queryLangCompile.js), and the session log runs it as written:
// it keeps a session one of whose requests matched the whole query
// (getSessionLog). Asked of sessions, each top-level clause tests the session
// as a whole instead: `tool:Bash file:deploy` keeps a session that ran Bash at
// one point and touched deploy at another, and `-tool:Bash` (or `tool!=Bash`)
// keeps one that never ran Bash. A group in parentheses is one clause, so
// `(tool:Bash file:deploy)` still asks for one request that did both.
//
// One grouped query answers it: each session's requests counted once per
// clause (a distinct count with the clause as its predicate), and HAVING keeps
// a session whose every clause counts above zero, or exactly zero for a
// negated one. Clauses on the machine or the person (endpoint:, identity:)
// hold for every request of a session alike, so they filter the rows as they
// are. A clause that mixes those with a request's own fields can only be asked
// of single requests, and is.
//
// PURE: parsing, serializing and spec assembly only, so it unit-tests in node.
import { parseQuery, serializeQuery } from "./queryLang.js";

// The most sessions one match lists, counts and draws. Past it the list holds
// the newest, and the count says more.
export const SESSION_MATCH_CAP = 1000;

const P = (name) => ({ stepAlias: "p", field: `DIMENSION_FIELD_PROMPT_${name}` });
const SUBTREE = "DIMENSION_FIELD_ILO_EVENT_CLUSTER_CLUSTER_SUBTREE";
// The same for every request of a session: its machine and its person.
const HELD = new Set(["ep", "u", "du"]);

// What a clause is called beside a session's count of it: the value it looks
// for, else the clause as written.
function clauseLabel(node) {
  if (node.type === "text") return `"${node.value}"`;
  if (node.type === "pred" && (node.op === "eq" || node.op === "in" || node.op === "contains")) {
    return Array.isArray(node.value) ? node.value.join(", ") : String(node.value);
  }
  return serializeQuery(node);
}

// The top-level clauses of a query, each in its positive form with whether it
// is negated: `-x`, `NOT x` and `x!=v` ask that no request of the session
// matches `x` (or `x:v`).
export function sessionClauses(queryText) {
  const { ast, errors } = parseQuery(queryText || "");
  if (!ast) return { clauses: [], errors };
  const clauses = [];
  for (const node of ast.type === "and" ? ast.children : [ast]) {
    if (node.type === "error") continue;
    let positive = node;
    let negated = false;
    if (node.type === "not") {
      positive = node.child;
      negated = true;
    } else if (node.type === "pred" && node.op === "ne") {
      positive = { ...node, op: Array.isArray(node.value) ? "in" : "eq" };
      negated = true;
    }
    const text = serializeQuery(positive);
    if (!text.trim()) continue;
    clauses.push({ text, negated, label: clauseLabel(positive) });
  }
  return { clauses, errors };
}

function eachPred(expr, visit) {
  if (!expr) return;
  if (expr.pred) visit(expr.pred);
  for (const c of expr.andExpr?.children || []) eachPred(c, visit);
  for (const c of expr.orExpr?.children || []) eachPred(c, visit);
  if (expr.notExpr) eachPred(expr.notExpr.child, visit);
}

// How a compiled clause is asked of a session: counted over its requests
// ("count": the request's own fields, and the topic subtree, which the planner
// admits in a distinct count's predicate), held by all its requests alike
// ("held": the machine, the person), or only of single requests ("request":
// it mixes the two).
export function clauseGrain(filter) {
  let count = 0;
  let held = 0;
  let other = 0;
  eachPred(filter, (pred) => {
    const alias = pred.field?.stepAlias;
    if (alias === "p" || (alias === "ec" && pred.field?.field === SUBTREE)) count++;
    else if (HELD.has(alias)) held++;
    else other++;
  });
  if (!other && !held) return "count";
  if (!other && !count) return "held";
  return "request";
}

const allOf = (parts) => (parts.length === 0 ? null : parts.length === 1 ? parts[0] : { andExpr: { children: parts } });

// The grouped query over sessions. `clauses`: [{ filter, negated, grain }],
// compiled; `scopeFilter` and `root` are the scope's, with the joins the
// clauses read already attached. Rows: one per matching session, newest
// activity first, at most `cap` + 1 (the extra one says there are more).
export function buildSessionMatchSpec({ root, timeRange, scopeFilter = null, clauses = [], cap = SESSION_MATCH_CAP }) {
  const counted = clauses.filter((c) => c.grain === "count");
  const where = scopeFilter ? [scopeFilter] : [];
  for (const c of clauses) {
    if (c.grain === "count") continue;
    where.push(c.negated ? { notExpr: { child: c.filter } } : c.filter);
  }
  // A session must have a request matching each positive clause, so only the
  // requests some counted clause speaks of are read: every one a count needs
  // (a negated clause's too) is among them.
  if (counted.some((c) => !c.negated)) {
    const any = counted.map((c) => c.filter);
    where.push(any.length === 1 ? any[0] : { orExpr: { children: any } });
  }
  const filter = allOf(where);
  return {
    root,
    timeRange,
    ...(filter ? { filter } : {}),
    output: {
      dimensions: [{ field: P("NOTIFICATION_ID"), outputAlias: "session_id" }],
      metrics: [
        { outputAlias: "last_time", computed: { field: P("EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MAX" } },
        { outputAlias: "first_time", computed: { field: P("EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MIN" } },
        { outputAlias: "requests", computed: { field: P("EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } },
        // One per session in practice: an agent session runs on one machine,
        // against one provider (and, more loosely, one model).
        { outputAlias: "agent", computed: { field: P("PROVIDER"), op: "AGGREGATE_OP_MAX" } },
        { outputAlias: "endpoint_id", computed: { field: P("ENDPOINT_ID"), op: "AGGREGATE_OP_MAX" } },
        { outputAlias: "model", computed: { field: P("MODEL"), op: "AGGREGATE_OP_MAX" } },
        ...counted.map((c, i) => ({
          outputAlias: `hits_${i}`,
          computed: { field: P("EVENT_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT", predicate: c.filter },
        })),
      ],
    },
    ...(counted.length
      ? { having: counted.map((c, i) => ({ metricAlias: `hits_${i}`, op: c.negated ? "OPERATOR_EQ" : "OPERATOR_GT", value: 0 })) }
      : {}),
    orderBy: [{ outputAlias: "last_time", direction: "DIRECTION_DESC" }],
    limit: cap + 1,
  };
}

// The rows a match query returned, as sessions: `hits` holds each counted
// clause's count of the session's requests, in the clauses' order (null for a
// clause not counted).
export function readSessionMatches(rows, clauses, { cap = SESSION_MATCH_CAP, parseTime = (v) => Date.parse(v) } = {}) {
  const countedAt = [];
  let n = 0;
  for (const c of clauses) countedAt.push(c.grain === "count" ? n++ : -1);
  const seen = new Set();
  const sessions = [];
  for (const row of rows || []) {
    const id = String(row?.session_id || "").toLowerCase();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    sessions.push({
      id,
      lastMs: row.last_time ? parseTime(row.last_time) : null,
      firstMs: row.first_time ? parseTime(row.first_time) : null,
      requests: Number(row.requests) || 0,
      agent: String(row.agent || ""),
      endpointId: String(row.endpoint_id || ""),
      model: String(row.model || ""),
      hits: countedAt.map((i) => (i < 0 ? null : Number(row[`hits_${i}`]) || 0)),
    });
  }
  const capped = sessions.length > cap;
  return { sessions: capped ? sessions.slice(0, cap) : sessions, capped };
}

// A short stable hash of the matched ids, so a changed set is a new run.
function idsKey(ids) {
  let h = 5381;
  for (const id of ids) for (let i = 0; i < id.length; i++) h = ((h * 33) ^ id.charCodeAt(i)) >>> 0;
  return `${ids.length}:${h.toString(36)}`;
}

// A `|>` aggregate run over whole matched sessions: its filter stage gives way
// to the sessions themselves, so `|> count by model` counts every request of
// the sessions the query kept, not only the requests that matched it.
export function kernelOverSessions(kernel, ids) {
  if (!kernel || !Array.isArray(ids)) return kernel;
  const steps = kernel.steps.map((step, i) => {
    if (i !== 0 || step.kind !== "analytics_query") return step;
    const { filter: _filter, ...spec } = step.spec;
    return {
      ...step,
      spec: {
        ...spec,
        root: { alias: "p", entity: "ENTITY_TYPE_PROMPT" },
        filter: { pred: { field: P("NOTIFICATION_ID"), op: "OPERATOR_IN", value: ids.length ? ids : ["00000000-0000-0000-0000-000000000000"] } },
      },
    };
  });
  return { ...kernel, id: `${kernel.id}#sessions:${idsKey(ids)}`, steps };
}

// The strings a query looks for in what sessions did, to find again in their
// records: the paths it names (file:) and the words it reads for (text:, or
// bare), never those it asks to be absent. A wildcard stands for anything, so
// its longest literal stretch is what is looked for; a string shorter than
// four characters would be found nearly everywhere, so it is left out.
export function searchStrings(queryText) {
  const { ast } = parseQuery(queryText || "");
  const out = [];
  const add = (v) => {
    const lit = String(v ?? "").split("*").map((s) => s.trim()).sort((a, b) => b.length - a.length)[0] || "";
    if (lit.length >= 4 && !out.includes(lit)) out.push(lit);
  };
  const walk = (node) => {
    if (!node || node.type === "not" || node.type === "error") return;
    if (node.type === "and" || node.type === "or") {
      node.children.forEach(walk);
      return;
    }
    if (node.type === "text") add(node.value);
    else if (node.type === "pred" && (node.key === "file" || node.key === "text") && node.op !== "ne") {
      for (const v of Array.isArray(node.value) ? node.value : [node.value]) add(v);
    }
  };
  walk(ast);
  return out;
}
